import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

function ErrorFallback({ error, errorInfo }: { error: Error | null; errorInfo: React.ErrorInfo | null }): React.ReactElement {
  let isDark = false;
  try {
    // Safe lookup in case ErrorBoundary is mounted outside ThemeProvider
    const themeContext = useTheme();
    isDark = themeContext ? themeContext.isDark : false;
  } catch (e) {
    // Fallback if useTheme context is unavailable
    isDark = false;
  }

  return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <div className={`max-w-md w-full rounded-lg shadow-lg p-6 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
        <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
        <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          The application encountered an error. Please check the browser console for details.
        </p>
        <details className="mb-4">
          <summary className={`cursor-pointer text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Error Details</summary>
          <pre className={`mt-2 text-xs p-2 rounded overflow-auto max-h-40 ${isDark ? 'bg-slate-700 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
            {error && error.toString()}
            {errorInfo && errorInfo.componentStack}
          </pre>
        </details>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary w-full"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} errorInfo={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
